import clsx from 'clsx';
import { useLayoutEffect, useRef } from 'react';
import { BiCheck } from 'react-icons/bi';

import s from './Popup.module.scss';

const EDGE_GAP = 8;

// The nearest ancestor that scrolls or clips is the content area the popup must stay in.
// Its content box, not its border box: the app shell's <main> pads its left edge by
// --rail-width and the fixed sidebar sits over that padding.
const getBoundsLeft = (el: HTMLElement) => {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (style.overflowX !== 'visible') {
      return node.getBoundingClientRect().left + node.clientLeft + parseFloat(style.paddingLeft);
    }
  }
  return 0;
};

/** Right-aligned to its anchor; flips to left-aligned when that would leave the content area. */
export const Popup = ({ children }: { children: React.ReactNode }) => {
  const ref = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || el.getBoundingClientRect().left >= getBoundsLeft(el) + EDGE_GAP) return;
    el.style.left = '0';
    el.style.right = 'auto';
  }, []);

  return (
    <section ref={ref} className={s.Popup}>
      {children}
    </section>
  );
};

export interface PopupSectionItem {
  label: string;
  onClick(): void;
  icon: React.ReactNode;
}

export const PopupSection = ({
  title,
  items,
  onItemClick,
}: {
  title: string;
  items: PopupSectionItem[];
  onItemClick?(): void;
}) => {
  return (
    <section>
      {title && <h6>{title}</h6>}
      <ul>
        {items.map(({ label, onClick, icon }) => (
          <li
            key={label}
            onClick={() => {
              onClick();
              onItemClick?.();
            }}
          >
            <div className={s.iconWrapper}>{icon}</div>
            <span>{label}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

export interface PopupCheckSectionMapItem {
  label: string;
}
export type PopupCheckSectionMap<T extends string> = Record<T, PopupCheckSectionMapItem>;

export const PopupCheckSection = <T extends string>({
  title,
  map,
  value,
  onChange,
  onItemClick,
}: {
  title: string;
  map: PopupCheckSectionMap<T>;
  value: T;
  onChange(v: T): void;
  onItemClick?(): void;
}) => {
  return (
    <section>
      {title && <h6>{title}</h6>}
      <ul>
        {Object.entries<PopupCheckSectionMapItem>(map).map(([key, { label }]) => (
          <li
            className={clsx(key !== value && s.notSelected)}
            key={key}
            onClick={() => {
              onChange(key as T);
              onItemClick?.();
            }}
          >
            <div className={s.iconWrapper}>{value === key && <BiCheck />}</div>
            <span>{label}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};
